import * as React from "react";
import { Box, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { UserEducation } from "../../types/education";
import {
  useAddUserEducationMutation,
  useUpdateUserEducationMutation,
} from "../../services/talent.service";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { BaseModal, Button, StyledTextField } from "../../components/global";

interface ModalUserEducationFormProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  data: UserEducation | null;
}

const ModalUserEducationForm = ({
  open,
  onClose,
  userId,
  data,
}: ModalUserEducationFormProps) => {
  const [add, { isLoading: adding }] = useAddUserEducationMutation();
  const [edit, { isLoading: editing }] = useUpdateUserEducationMutation();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      payload.user_id = userId;

      if (!!payload.start_date)
        payload.start_date = dayjs(payload.start_date).format("YYYY-MM-DD");
      if (!!payload.end_date)
        payload.end_date = dayjs(payload.end_date).format("YYYY-MM-DD");

      if (!data) {
        await add(payload).unwrap();
        toast.success("Education added");
      } else {
        payload.id = data?.id!;
        await edit(payload).unwrap();
        toast.success("Education updated");
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("school", data?.school || "");
      setValue("degree", data?.degree || "");
      setValue("field_of_study", data?.field_of_study || "");
      setValue("start_date", data?.start_date ? dayjs(data.start_date) : null);
      setValue("end_date", data?.end_date ? dayjs(data.end_date) : null);
    }
  }, [data, open, setValue]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Create" : "Edit"} User Education`}
      maxWidth="sm"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={adding || editing}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Add" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="school"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="School Name"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.school}
                  helperText={!!errors.school && "This field is required"}
                />
              )}
            />
            <Controller
              name="degree"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Degree (optional)"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                />
              )}
            />
            <Controller
              name="field_of_study"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Field Of Study (optional)"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                />
              )}
            />
            <Stack direction="row" spacing={2}>
              <Controller
                name="start_date"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DesktopDatePicker
                    label="Start Date"
                    inputFormat="MMM DD, YYYY"
                    value={value || null}
                    showToolbar={true}
                    onChange={onChange}
                    renderInput={(params) => (
                      <StyledTextField {...params} fullWidth />
                    )}
                  />
                )}
              />
              <Controller
                name="end_date"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DesktopDatePicker
                    label="End Date"
                    inputFormat="MMM DD, YYYY"
                    value={value || null}
                    showToolbar={true}
                    onChange={onChange}
                    renderInput={(params) => (
                      <StyledTextField {...params} fullWidth />
                    )}
                  />
                )}
              />
            </Stack>
          </Stack>
        </Box>
      </form>
    </BaseModal>
  );
};
export default ModalUserEducationForm;
